import nftMarketplace from "../contracts/NFTMarketplace.json";
import nftMarketplaceV2 from "../contracts/NFTMarketplaceV2.json";

export const NFT_MARKETPLACE_ABI = nftMarketplace.abi;
export const NFT_MARKETPLACE_V2_ABI = nftMarketplaceV2.abi;

// Pinata IPFS Configuration — public read gateway only. Pinning (writes)
// goes through the authenticated backend proxy (services/ipfsService.js);
// no Pinata credentials may ever appear in client code.
export const PINATA_CONFIG = {
  GATEWAY_URL: "https://beige-sophisticated-scorpion-932.mypinata.cloud/ipfs/",
};

// Alchemy Configuration
export const ALCHEMY_CONFIG = {
  POLYGON: {
    apiKey: process.env.NEXT_PUBLIC_ALCHEMY_API_KEY_POLYGON,
    network: "polygon-mainnet",
    rpcUrl: process.env.NEXT_PUBLIC_ALCHEMY_API_KEY_POLYGON
      ? `https://polygon-mainnet.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY_POLYGON}`
      : "https://polygon-bor-rpc.publicnode.com",
  },
  AMOY: {
    apiKey: process.env.NEXT_PUBLIC_ALCHEMY_API_KEY_AMOY,
    network: "polygon-amoy",
    rpcUrl: process.env.NEXT_PUBLIC_ALCHEMY_API_KEY_AMOY
      ? `https://polygon-amoy.g.alchemy.com/v2/${process.env.NEXT_PUBLIC_ALCHEMY_API_KEY_AMOY}`
      : "https://polygon-amoy-bor-rpc.publicnode.com",
  },
};

// Network Configuration (Updated with Alchemy RPC URLs)
// `walletParams` is the wallet_addEthereumChain payload for each chain, used
// when prompting the user's wallet to switch/add the target network.
export const SUPPORTED_NETWORKS = {
  LOCALHOST: {
    chainId: 31337,
    name: "Hardhat Localhost",
    rpcUrl: "http://127.0.0.1:8545",
    walletParams: {
      chainId: "0x7a69",
      chainName: "Hardhat Localhost",
      rpcUrls: ["http://127.0.0.1:8545"],
      nativeCurrency: { name: "Ethereum", symbol: "ETH", decimals: 18 },
    },
  },
  POLYGON: {
    chainId: 137,
    name: "Polygon Mainnet",
    rpcUrl: ALCHEMY_CONFIG.POLYGON.rpcUrl,
    alchemyConfig: ALCHEMY_CONFIG.POLYGON,
    walletParams: {
      chainId: "0x89",
      chainName: "Polygon Mainnet",
      rpcUrls: ["https://polygon-bor-rpc.publicnode.com"],
      blockExplorerUrls: ["https://polygonscan.com"],
      nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 },
    },
  },
  POLYGON_AMOY: {
    chainId: 80002,
    name: "Polygon Amoy Testnet",
    rpcUrl: ALCHEMY_CONFIG.AMOY.rpcUrl,
    alchemyConfig: ALCHEMY_CONFIG.AMOY,
    walletParams: {
      chainId: "0x13882",
      chainName: "Polygon Amoy Testnet",
      rpcUrls: ["https://polygon-amoy-bor-rpc.publicnode.com"],
      blockExplorerUrls: ["https://amoy.polygonscan.com"],
      nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 },
    },
  },
};

// The single network this build transacts on. One env var decides it:
// NEXT_PUBLIC_TARGET_CHAIN_ID (137 = Polygon mainnet, 80002 = Amoy testnet,
// 31337 = local Hardhat). Defaults to Polygon mainnet.
const TARGET_CHAIN_ID = Number(process.env.NEXT_PUBLIC_TARGET_CHAIN_ID) || 137;

export const TARGET_NETWORK =
  Object.values(SUPPORTED_NETWORKS).find(
    (network) => network.chainId === TARGET_CHAIN_ID,
  ) || SUPPORTED_NETWORKS.POLYGON;

// Legacy alias — older code reads DEFAULT_NETWORK; both now mean "the one
// configured target network".
export const DEFAULT_NETWORK = TARGET_NETWORK;

// Marketplace contract address for the target network. Env overrides; the
// per-network fallbacks are the known deployments — Polygon mainnet's is the
// live NFTMarketplace ("Metaverse Tokens"), localhost's is Hardhat's
// deterministic first-deploy address. Other networks require the env var.
const KNOWN_MARKETPLACE_ADDRESSES = {
  [SUPPORTED_NETWORKS.POLYGON.chainId]:
    "0x98e875257383C97EA7761bfD3C55de34c1002714",
  [SUPPORTED_NETWORKS.LOCALHOST.chainId]:
    "0x5FbDB2315678afecb367f032d93F642f64180aa3",
};

export const NFT_MARKETPLACE_ADDRESS =
  process.env.NEXT_PUBLIC_NFT_MARKETPLACE_ADDRESS ||
  KNOWN_MARKETPLACE_ADDRESSES[TARGET_NETWORK.chainId] ||
  "";

// Marketplace V2 (escrow + card-sale fulfillment). Fiat (card) listings are
// created here; V1 remains the crypto marketplace for existing tokens. No
// production deployment exists yet, so the address always comes from the env;
// the only fallback is localhost's deterministic second-deploy address
// (scripts/deploy.js deploys V1 first, then V2).
const KNOWN_MARKETPLACE_V2_ADDRESSES = {
  [SUPPORTED_NETWORKS.LOCALHOST.chainId]:
    "0xe7f1725E7734CE288F8367e1Bb143E90bb3F0512",
};

export const NFT_MARKETPLACE_V2_ADDRESS =
  process.env.NEXT_PUBLIC_NFT_MARKETPLACE_V2_ADDRESS ||
  KNOWN_MARKETPLACE_V2_ADDRESSES[TARGET_NETWORK.chainId] ||
  "";

// Block at which each marketplace contract was deployed, tagged onto each entry
// from getReadContracts. localhost is 0 (Hardhat starts fresh each run); on
// public chains it's null unless set via env. Kept as config for contract reads
// that want a starting block; dashboard stats no longer scan event logs (they
// come from the backend DB), so nothing here bounds a chain scan anymore.
//   NEXT_PUBLIC_NFT_MARKETPLACE_DEPLOY_BLOCK     — V1 deploy block
//   NEXT_PUBLIC_NFT_MARKETPLACE_V2_DEPLOY_BLOCK  — V2 deploy block
const parseDeployBlock = (raw) => {
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : null;
};

export const NFT_MARKETPLACE_DEPLOY_BLOCK =
  parseDeployBlock(process.env.NEXT_PUBLIC_NFT_MARKETPLACE_DEPLOY_BLOCK) ??
  (TARGET_NETWORK.chainId === SUPPORTED_NETWORKS.LOCALHOST.chainId ? 0 : null);

export const NFT_MARKETPLACE_V2_DEPLOY_BLOCK =
  parseDeployBlock(process.env.NEXT_PUBLIC_NFT_MARKETPLACE_V2_DEPLOY_BLOCK) ??
  (TARGET_NETWORK.chainId === SUPPORTED_NETWORKS.LOCALHOST.chainId ? 0 : null);

// Where NEW mints/listings land. One env var flips it:
// NEXT_PUBLIC_PRIMARY_LISTING_CONTRACT = "v1" | "v2" (default "v1").
// "v1"/unset keeps today's behavior byte-for-byte (mainnet stays on V1 until
// V2 is deployed there and this flag is flipped). "v2" routes new crypto mints
// to V2 — but ONLY if a V2 address is actually configured for this chain;
// otherwise we hard-fall-back to V1 so a mint can never target an empty
// address. Existing tokens are NEVER moved by this flag: it only chooses the
// target for a fresh mint. Reads always span both contracts regardless.
const PRIMARY_LISTING_PREFERENCE = (
  process.env.NEXT_PUBLIC_PRIMARY_LISTING_CONTRACT || "v1"
).toLowerCase();

const primaryIsV2 =
  PRIMARY_LISTING_PREFERENCE === "v2" && Boolean(NFT_MARKETPLACE_V2_ADDRESS);

if (
  PRIMARY_LISTING_PREFERENCE === "v2" &&
  !NFT_MARKETPLACE_V2_ADDRESS &&
  typeof window !== "undefined"
) {
  console.warn(
    "[nftMarketplace] PRIMARY_LISTING_CONTRACT=v2 but no V2 address is configured for this chain; falling back to V1 for new mints.",
  );
}

export const PRIMARY_LISTING_ADDRESS = primaryIsV2
  ? NFT_MARKETPLACE_V2_ADDRESS
  : NFT_MARKETPLACE_ADDRESS;

export const PRIMARY_LISTING_ABI = primaryIsV2
  ? NFT_MARKETPLACE_V2_ABI
  : NFT_MARKETPLACE_ABI;

// True when a new mint should carry the V2 6-arg createToken(fiatOnly=false).
export const PRIMARY_LISTING_IS_V2 = primaryIsV2;

// Resolve the {address, abi} pair for a token's OWN contract. Matching is by
// address so every on-chain action routes to the contract that actually holds
// the token — a V1 token never receives a V2 call and vice versa. SAFE DEFAULT
// when the address is unknown/absent = V1: legacy links and pre-V2 cache
// predate V2 so are necessarily V1 tokens, and every V2 token is born tagged
// (mint returns its address, reads tag it, its detail id is 3-part), so an
// untagged item is provably V1 and can never reach this default incorrectly.
export const resolveContractConfig = (contractAddress) => {
  if (
    contractAddress &&
    NFT_MARKETPLACE_V2_ADDRESS &&
    contractAddress.toLowerCase() === NFT_MARKETPLACE_V2_ADDRESS.toLowerCase()
  ) {
    return { address: NFT_MARKETPLACE_V2_ADDRESS, abi: NFT_MARKETPLACE_V2_ABI };
  }
  return { address: NFT_MARKETPLACE_ADDRESS, abi: NFT_MARKETPLACE_ABI };
};

// The marketplace contracts to READ on this chain. V2 is always included when
// configured; V1 only when its address is non-empty (Amoy has no V1 → its read
// is skipped cleanly rather than querying an empty address). Reads loop this
// list and tag each item with the contract it came from.
export const getReadContracts = () => {
  const contracts = [];
  if (NFT_MARKETPLACE_ADDRESS) {
    contracts.push({
      address: NFT_MARKETPLACE_ADDRESS,
      abi: NFT_MARKETPLACE_ABI,
      deployBlock: NFT_MARKETPLACE_DEPLOY_BLOCK,
    });
  }
  if (NFT_MARKETPLACE_V2_ADDRESS) {
    contracts.push({
      address: NFT_MARKETPLACE_V2_ADDRESS,
      abi: NFT_MARKETPLACE_V2_ABI,
      deployBlock: NFT_MARKETPLACE_V2_DEPLOY_BLOCK,
    });
  }
  return contracts;
};

// Utility function to get RPC URL by chain ID
export const getRpcUrlByChainId = (chainId) => {
  const network = Object.values(SUPPORTED_NETWORKS).find(
    (network) => network.chainId === chainId,
  );
  return network?.rpcUrl || "http://127.0.0.1:8545";
};

// Utility function to get Alchemy config by chain ID
export const getAlchemyConfigByChainId = (chainId) => {
  const network = Object.values(SUPPORTED_NETWORKS).find(
    (network) => network.chainId === chainId,
  );
  return network?.alchemyConfig || null;
};
